import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Tooltip,
  Box,
  CircularProgress,
  Grid,
  Paper,
  Typography,
  Tabs,
  Tab,
  Divider,
  Card,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AssignmentIcon from "@mui/icons-material/Assignment";
import PendingIcon from "@mui/icons-material/Pending";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  ArcElement,
} from "chart.js";
import { Pie } from "react-chartjs-2";
import { Line } from "react-chartjs-2";
import {
  PointElement,
  LineElement,
  Filler,
} from "chart.js";
import { API_ENDPOINTS } from "config/api";
import axiosInstance from "API/axiosInstance";
import safeToast from "utils/safeToast";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  ChartTooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement,
  Filler
);

const UnknownAlumniRegistrationDetailsDialog = ({ open, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [activeTab, setActiveTab] = useState(0);

  const fetchAnalytics = useCallback(async () => {
    try {
      setLoading(true);
      console.log("Fetching analytics from:", API_ENDPOINTS.UNKNOWN_ALUMNI.GET_ANALYTICS);
      const response = await axiosInstance.get(API_ENDPOINTS.UNKNOWN_ALUMNI.GET_ANALYTICS);
      console.log("Analytics response:", response);
      console.log("Analytics data:", response.data);
      
      // Handle different response structures (API returns camelCase due to JsonNamingPolicy.CamelCase)
      const analyticsData = response.data?.data || response.data;
      
      if (!analyticsData) {
        console.warn("No analytics data received");
        safeToast.warn("No data available");
        setAnalytics(null);
        return;
      }
      
      // Backend returns camelCase, but we'll support both for compatibility
      const stats = analyticsData.statistics || analyticsData.Statistics || {};
      const formattedAnalytics = {
        Statistics: {
          TotalRequests: stats.totalRequests || stats.TotalRequests || 0,
          Pending: stats.pending || stats.Pending || 0,
          Approved: stats.approved || stats.Approved || 0,
          Cancelled: stats.cancelled || stats.Cancelled || 0,
        },
        StatusBreakdown: analyticsData.statusBreakdown || analyticsData.StatusBreakdown || [],
        RequestsByDate: analyticsData.requestsByDate || analyticsData.RequestsByDate || [],
        RequestsByGender: analyticsData.requestsByGender || analyticsData.RequestsByGender || [],
        RequestsByBatch: analyticsData.requestsByBatch || analyticsData.RequestsByBatch || [],
        RequestsByCollege: analyticsData.requestsByCollege || analyticsData.RequestsByCollege || [],
        RequestsByCourse: analyticsData.requestsByCourse || analyticsData.RequestsByCourse || [],
      };
      
      console.log("Raw analytics data:", analyticsData);
      console.log("Formatted analytics:", formattedAnalytics);
      setAnalytics(formattedAnalytics);
    } catch (error) {
      console.error("Error fetching analytics:", error);
      console.error("Error response:", error.response);
      console.error("Error message:", error.message);
      safeToast.error(
        error.response?.data?.message || 
        error.message || 
        "Failed to fetch analytics data. Please try again."
      );
      setAnalytics(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      fetchAnalytics();
    }
  }, [open, fetchAnalytics]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Status Pie Chart Data
  const getStatusPieChartData = () => {
    const statusBreakdown = analytics?.StatusBreakdown || analytics?.statusBreakdown || [];
    if (!statusBreakdown || statusBreakdown.length === 0) return null;

    const colors = {
      Pending: "#FFA726",
      Approved: "#66BB6A",
      Rejected: "#EF5350",
      Cancelled: "#78909C",
      Unknown: "#BDBDBD",
    };

    return {
      labels: statusBreakdown.map((item) => item.Status || item.status || "Unknown"),
      datasets: [
        {
          label: "Status Distribution",
          data: statusBreakdown.map((item) => item.Count || item.count || 0),
          backgroundColor: statusBreakdown.map(
            (item) => colors[item.Status || item.status] || "#BDBDBD"
          ),
          borderColor: "#fff",
          borderWidth: 2,
        },
      ],
    };
  };

  // Requests by Date Line Chart Data
  const getDateLineChartData = () => {
    const requestsByDate = analytics?.RequestsByDate || analytics?.requestsByDate || [];
    if (!requestsByDate || requestsByDate.length === 0) return null;

    const sortedData = [...requestsByDate].reverse();

    return {
      labels: sortedData.map((item) => {
        const dateStr = item.Date || item.date;
        if (!dateStr) return "";
        const date = new Date(dateStr);
        return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
      }),
      datasets: [
        {
          label: "Requests",
          data: sortedData.map((item) => item.Count || item.count || 0),
          borderColor: "#1976d2",
          backgroundColor: "rgba(25, 118, 210, 0.1)",
          fill: true,
          tension: 0.4,
        },
      ],
    };
  };

  // Gender Bar Chart Data
  const getGenderBarChartData = () => {
    const requestsByGender = analytics?.RequestsByGender || analytics?.requestsByGender || [];
    if (!requestsByGender || requestsByGender.length === 0) return null;

    return {
      labels: requestsByGender.map((item) => item.Gender || item.gender || "Not Specified"),
      datasets: [
        {
          label: "Requests by Gender",
          data: requestsByGender.map((item) => item.Count || item.count || 0),
          backgroundColor: ["#42A5F5", "#AB47BC", "#26A69A"],
          borderColor: "#fff",
          borderWidth: 1,
        },
      ],
    };
  };

  // Batch Bar Chart Data
  const getBatchBarChartData = () => {
    const requestsByBatch = analytics?.RequestsByBatch || analytics?.requestsByBatch || [];
    if (!requestsByBatch || requestsByBatch.length === 0) return null;

    return {
      labels: requestsByBatch.map((item) => item.Batch || item.batch || "Not Specified"),
      datasets: [
        {
          label: "Requests by Batch",
          data: requestsByBatch.map((item) => item.Count || item.count || 0),
          backgroundColor: "#66BB6A",
          borderColor: "#fff",
          borderWidth: 1,
        },
      ],
    };
  };

  // College Bar Chart Data
  const getCollegeBarChartData = () => {
    const requestsByCollege = analytics?.RequestsByCollege || analytics?.requestsByCollege || [];
    if (!requestsByCollege || requestsByCollege.length === 0) return null;

    return {
      labels: requestsByCollege.map((item) => {
        const college = item.College || item.college || "Not Specified";
        return college.length > 20 ? college.substring(0, 20) + "..." : college;
      }),
      datasets: [
        {
          label: "Requests by College",
          data: requestsByCollege.map((item) => item.Count || item.count || 0),
          backgroundColor: "#FFA726",
          borderColor: "#fff",
          borderWidth: 1,
        },
      ],
    };
  };

  // Course Bar Chart Data
  const getCourseBarChartData = () => {
    const requestsByCourse = analytics?.RequestsByCourse || analytics?.requestsByCourse || [];
    if (!requestsByCourse || requestsByCourse.length === 0) return null;

    return {
      labels: requestsByCourse.map((item) => {
        const course = item.Course || item.course || "Not Specified";
        return course.length > 20 ? course.substring(0, 20) + "..." : course;
      }),
      datasets: [
        {
          label: "Requests by Course",
          data: requestsByCourse.map((item) => item.Count || item.count || 0),
          backgroundColor: "#26C6DA",
          borderColor: "#fff",
          borderWidth: 1,
        },
      ],
    };
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top",
      },
      tooltip: {
        mode: "index",
        intersect: false,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 1,
        },
      },
    },
  };

  const pieChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "right",
      },
    },
  };

  const lineChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        mode: "index",
        intersect: false,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 1,
        },
      },
    },
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ pr: 8, fontWeight: "bold", color: "primary.main" }}>
        Unknown Alumni Registration Details & Analytics
        <Box sx={{ position: "absolute", top: 8, right: 8 }}>
          <Tooltip title="Close">
            <IconButton onClick={onClose} color="error">
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", p: 4 }}>
            <CircularProgress />
          </Box>
        ) : analytics ? (
          <>
            {/* Statistics Cards */}
            <Box sx={{ p: 3, bgcolor: "background.default" }}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6} lg={3}>
                  <MDBox mb={1.5}>
                    <Card>
                      <MDBox display="flex" justifyContent="space-between" pt={1} px={2}>
                        <MDBox
                          variant="gradient"
                          bgColor="primary"
                          color="white"
                          coloredShadow="primary"
                          borderRadius="xl"
                          display="flex"
                          justifyContent="center"
                          alignItems="center"
                          width="4rem"
                          height="4rem"
                          mt={-3}
                        >
                          <AssignmentIcon sx={{ fontSize: 24 }} />
                        </MDBox>
                        <MDBox textAlign="right" lineHeight={1.25}>
                          <MDTypography variant="button" fontWeight="light" color="text">
                            Total Requests
                          </MDTypography>
                          <MDTypography variant="h4" fontWeight="bold">
                            {analytics.Statistics?.TotalRequests || analytics.Statistics?.totalRequests || 0}
                          </MDTypography>
                        </MDBox>
                      </MDBox>
                      <Divider />
                      <MDBox pb={2} px={2}>
                        <MDTypography component="p" variant="button" color="text">
                          All registration requests
                        </MDTypography>
                      </MDBox>
                    </Card>
                  </MDBox>
                </Grid>
                <Grid item xs={12} md={6} lg={3}>
                  <MDBox mb={1.5}>
                    <Card>
                      <MDBox display="flex" justifyContent="space-between" pt={1} px={2}>
                        <MDBox
                          variant="gradient"
                          bgColor="warning"
                          color="white"
                          coloredShadow="warning"
                          borderRadius="xl"
                          display="flex"
                          justifyContent="center"
                          alignItems="center"
                          width="4rem"
                          height="4rem"
                          mt={-3}
                        >
                          <PendingIcon sx={{ fontSize: 24 }} />
                        </MDBox>
                        <MDBox textAlign="right" lineHeight={1.25}>
                          <MDTypography variant="button" fontWeight="light" color="text">
                            Pending
                          </MDTypography>
                          <MDTypography variant="h4" fontWeight="bold">
                            {analytics.Statistics?.Pending || analytics.Statistics?.pending || 0}
                          </MDTypography>
                        </MDBox>
                      </MDBox>
                      <Divider />
                      <MDBox pb={2} px={2}>
                        <MDTypography component="p" variant="button" color="text">
                          Awaiting review
                        </MDTypography>
                      </MDBox>
                    </Card>
                  </MDBox>
                </Grid>
                <Grid item xs={12} md={6} lg={3}>
                  <MDBox mb={1.5}>
                    <Card>
                      <MDBox display="flex" justifyContent="space-between" pt={1} px={2}>
                        <MDBox
                          variant="gradient"
                          bgColor="success"
                          color="white"
                          coloredShadow="success"
                          borderRadius="xl"
                          display="flex"
                          justifyContent="center"
                          alignItems="center"
                          width="4rem"
                          height="4rem"
                          mt={-3}
                        >
                          <CheckCircleIcon sx={{ fontSize: 24 }} />
                        </MDBox>
                        <MDBox textAlign="right" lineHeight={1.25}>
                          <MDTypography variant="button" fontWeight="light" color="text">
                            Approved
                          </MDTypography>
                          <MDTypography variant="h4" fontWeight="bold">
                            {analytics.Statistics?.Approved || analytics.Statistics?.approved || 0}
                          </MDTypography>
                        </MDBox>
                      </MDBox>
                      <Divider />
                      <MDBox pb={2} px={2}>
                        <MDTypography component="p" variant="button" color="text">
                          Successfully approved
                        </MDTypography>
                      </MDBox>
                    </Card>
                  </MDBox>
                </Grid>
                <Grid item xs={12} md={6} lg={3}>
                  <MDBox mb={1.5}>
                    <Card>
                      <MDBox display="flex" justifyContent="space-between" pt={1} px={2}>
                        <MDBox
                          variant="gradient"
                          bgColor="error"
                          color="white"
                          coloredShadow="error"
                          borderRadius="xl"
                          display="flex"
                          justifyContent="center"
                          alignItems="center"
                          width="4rem"
                          height="4rem"
                          mt={-3}
                        >
                          <CancelIcon sx={{ fontSize: 24 }} />
                        </MDBox>
                        <MDBox textAlign="right" lineHeight={1.25}>
                          <MDTypography variant="button" fontWeight="light" color="text">
                            Cancelled
                          </MDTypography>
                          <MDTypography variant="h4" fontWeight="bold">
                            {analytics.Statistics?.Cancelled || analytics.Statistics?.cancelled || 0}
                          </MDTypography>
                        </MDBox>
                      </MDBox>
                      <Divider />
                      <MDBox pb={2} px={2}>
                        <MDTypography component="p" variant="button" color="text">
                          Cancelled requests
                        </MDTypography>
                      </MDBox>
                    </Card>
                  </MDBox>
                </Grid>
              </Grid>
            </Box>

            <Divider />

            {/* Tabs for different chart views */}
            <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
              <Tabs value={activeTab} onChange={handleTabChange} variant="scrollable" scrollButtons="auto">
                <Tab label="Status Overview" />
                <Tab label="Trends" />
                <Tab label="Demographics" />
                <Tab label="Academic" />
              </Tabs>
            </Box>

            {/* Tab Content */}
            <Box sx={{ p: 3 }}>
              {activeTab === 0 && (
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 2, height: 400 }}>
                      <Typography variant="h6" gutterBottom>
                        Status Distribution
                      </Typography>
                      {getStatusPieChartData() ? (
                        <Box sx={{ height: 350 }}>
                          <Pie data={getStatusPieChartData()} options={pieChartOptions} />
                        </Box>
                      ) : (
                        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 350 }}>
                          <Typography color="text.secondary">No data available</Typography>
                        </Box>
                      )}
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 2, height: 400 }}>
                      <Typography variant="h6" gutterBottom>
                        Requests by Gender
                      </Typography>
                      {getGenderBarChartData() ? (
                        <Box sx={{ height: 350 }}>
                          <Bar data={getGenderBarChartData()} options={chartOptions} />
                        </Box>
                      ) : (
                        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 350 }}>
                          <Typography color="text.secondary">No data available</Typography>
                        </Box>
                      )}
                    </Paper>
                  </Grid>
                </Grid>
              )}

              {activeTab === 1 && (
                <Paper sx={{ p: 2, height: 400 }}>
                  <Typography variant="h6" gutterBottom>
                    Requests Trend (Last 30 Days)
                  </Typography>
                  {getDateLineChartData() ? (
                    <Box sx={{ height: 350 }}>
                      <Line data={getDateLineChartData()} options={lineChartOptions} />
                    </Box>
                  ) : (
                    <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 350 }}>
                      <Typography color="text.secondary">No data available</Typography>
                    </Box>
                  )}
                </Paper>
              )}

              {activeTab === 2 && (
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Paper sx={{ p: 2, height: 400 }}>
                      <Typography variant="h6" gutterBottom>
                        Requests by Gender
                      </Typography>
                      {getGenderBarChartData() ? (
                        <Box sx={{ height: 350 }}>
                          <Bar data={getGenderBarChartData()} options={chartOptions} />
                        </Box>
                      ) : (
                        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 350 }}>
                          <Typography color="text.secondary">No data available</Typography>
                        </Box>
                      )}
                    </Paper>
                  </Grid>
                </Grid>
              )}

              {activeTab === 3 && (
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 2, height: 400 }}>
                      <Typography variant="h6" gutterBottom>
                        Top 10 Batches
                      </Typography>
                      {getBatchBarChartData() ? (
                        <Box sx={{ height: 350 }}>
                          <Bar data={getBatchBarChartData()} options={chartOptions} />
                        </Box>
                      ) : (
                        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 350 }}>
                          <Typography color="text.secondary">No data available</Typography>
                        </Box>
                      )}
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 2, height: 400 }}>
                      <Typography variant="h6" gutterBottom>
                        Top 10 Colleges
                      </Typography>
                      {getCollegeBarChartData() ? (
                        <Box sx={{ height: 350 }}>
                          <Bar data={getCollegeBarChartData()} options={chartOptions} />
                        </Box>
                      ) : (
                        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 350 }}>
                          <Typography color="text.secondary">No data available</Typography>
                        </Box>
                      )}
                    </Paper>
                  </Grid>
                  <Grid item xs={12}>
                    <Paper sx={{ p: 2, height: 400 }}>
                      <Typography variant="h6" gutterBottom>
                        Top 10 Courses
                      </Typography>
                      {getCourseBarChartData() ? (
                        <Box sx={{ height: 350 }}>
                          <Bar data={getCourseBarChartData()} options={chartOptions} />
                        </Box>
                      ) : (
                        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 350 }}>
                          <Typography color="text.secondary">No data available</Typography>
                        </Box>
                      )}
                    </Paper>
                  </Grid>
                </Grid>
              )}
            </Box>
          </>
        ) : (
          <Box sx={{ p: 4, textAlign: "center" }}>
            <Typography color="text.secondary">No data available</Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

UnknownAlumniRegistrationDetailsDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default UnknownAlumniRegistrationDetailsDialog;

